(() => {
  // Prevent duplicate execution if script tag is defer-loaded multiple times
  if (window.__mimoyaPocketShelfLoaded) return;
  window.__mimoyaPocketShelfLoaded = true;

  const VISIBLE_COUNT = 4;

  /**
   * Fisher-Yates shuffle returning a copy of the array.
   */
  function fisherYatesShuffle(arr) {
    const copy = arr.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = copy[i];
      copy[i] = copy[j];
      copy[j] = temp;
    }
    return copy;
  }

  /**
   * Check if two index sets contain exactly the same elements.
   */
  function setsAreEqual(a, b) {
    if (a.length !== b.length) return false;
    const sortedA = a.slice().sort();
    const sortedB = b.slice().sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }

  /**
   * Pick `count` distinct indices from `allIndices`.
   * When avoiding `currentSet` and total count > count, ensures the new set is not identical.
   */
  function pickIndices(allIndices, count, currentSet) {
    if (allIndices.length <= count) return allIndices.slice();

    let attempts = 0;
    while (attempts < 20) {
      const shuffled = fisherYatesShuffle(allIndices);
      const chosen = shuffled.slice(0, count);
      if (!currentSet || !setsAreEqual(chosen, currentSet)) {
        return chosen;
      }
      attempts++;
    }
    return fisherYatesShuffle(allIndices).slice(0, count);
  }

  /**
   * Apply selected pair indices to a shelves layout shelf.
   */
  function applySelection(shelfRoot, chosenIndices) {
    const productsGrid = shelfRoot.querySelector('.m-shelf__products');
    const footersGrid = shelfRoot.querySelector('.m-shelf__footers');
    if (!productsGrid) return;

    const topItems = shelfRoot.querySelectorAll('.m-shelf__item[data-shelf-index]');
    const footItems = footersGrid ? shelfRoot.querySelectorAll('.m-shelf__footer-item[data-shelf-index]') : [];

    const chosenMap = new Map();
    chosenIndices.forEach((idxStr, orderIdx) => {
      chosenMap.set(String(idxStr), orderIdx + 1);
    });

    topItems.forEach((topItem) => {
      const idx = topItem.getAttribute('data-shelf-index');
      if (chosenMap.has(idx)) {
        topItem.removeAttribute('hidden');
        topItem.style.order = chosenMap.get(idx);
      } else {
        topItem.setAttribute('hidden', '');
        topItem.style.order = '';
      }
    });

    footItems.forEach((footItem) => {
      const idx = footItem.getAttribute('data-shelf-index');
      if (chosenMap.has(idx)) {
        footItem.removeAttribute('hidden');
        footItem.style.order = chosenMap.get(idx);
      } else {
        footItem.setAttribute('hidden', '');
        footItem.style.order = '';
      }
    });

    shelfRoot._currentIndices = chosenIndices.map(String);
  }

  /**
   * Top-up shelves (cart page / drawer): forms carry data-shelf-index; the first
   * `data-visible-count` are visible, the rest wait hidden in the pool.
   */
  function refreshTopup(root) {
    const forms = Array.from(root.querySelectorAll('.m-shelf__topup-form[data-shelf-index]'));
    const visible = parseInt(root.getAttribute('data-visible-count') || '2', 10);
    if (forms.length <= visible) return;
    const current = forms.filter((f) => !f.hasAttribute('hidden')).map((f) => f.getAttribute('data-shelf-index'));
    const all = forms.map((f) => f.getAttribute('data-shelf-index'));
    const chosen = pickIndices(all, visible, current).map(String);
    forms.forEach((f) => {
      if (chosen.indexOf(f.getAttribute('data-shelf-index')) > -1) f.removeAttribute('hidden');
      else f.setAttribute('hidden', '');
    });
  }

  function announce(root) {
    const liveStatus = root.querySelector('[data-shelf-status]');
    if (!liveStatus) return;
    const message = root.getAttribute('data-refreshed-message') || '';
    liveStatus.textContent = '';
    requestAnimationFrame(() => { liveStatus.textContent = message; });
  }

  function initShelf(shelfRoot) {
    const topItems = shelfRoot.querySelectorAll('.m-shelf__item[data-shelf-index]');
    if (topItems.length <= VISIBLE_COUNT) return;
    const allIndices = Array.from(topItems).map((el) => el.getAttribute('data-shelf-index'));
    applySelection(shelfRoot, pickIndices(allIndices, VISIBLE_COUNT, shelfRoot._currentIndices));
  }

  function initAllShelves() {
    document.querySelectorAll("[data-pocket-shelf][data-shelf-layout='shelves']").forEach(initShelf);
  }

  // Delegated so shelves swapped in by the cart Section Rendering API keep working.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-shelf-refresh]');
    if (!btn) return;
    const root = btn.closest('[data-pocket-shelf]');
    if (!root) return;
    e.preventDefault();
    if (root.getAttribute('data-shelf-layout') === 'topup') {
      refreshTopup(root);
    } else {
      const topItems = root.querySelectorAll('.m-shelf__item[data-shelf-index]');
      const allIndices = Array.from(topItems).map((el) => el.getAttribute('data-shelf-index'));
      applySelection(root, pickIndices(allIndices, VISIBLE_COUNT, root._currentIndices));
    }
    btn.focus();
    announce(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllShelves);
  } else {
    initAllShelves();
  }

  // Back/forward cache restores also reshuffle the home shelf.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initAllShelves();
  });

  // Theme editor: re-shuffle a shelf that Shopify just re-rendered.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) event.target.querySelectorAll("[data-pocket-shelf][data-shelf-layout='shelves']").forEach(initShelf);
  });
})();
